import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { contactContent, footerSettings } from '@/hooks/useContent';

const Contact = () => {
  const content = contactContent;
  const contactInfo = footerSettings.contact;
  const businessHours = footerSettings.business_hours;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    serviceType: '',
    packageType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // For Netlify Forms, encode the data and submit
    const formBody = new URLSearchParams({
      'form-name': 'contact',
      ...formData
    }).toString();

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });

      if (response.ok) {
        // Redirect to success page
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error("Failed to Send Enquiry", {
        description: `Please try again or contact us directly at ${contactInfo.email}`,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="py-24" 
      style={{ backgroundColor: content.background?.color || '#f7f5f2' }}
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="section-title text-4xl md:text-5xl font-bold mb-4" data-testid="contact-title">
            {content.title}
          </h2>
          <p className="section-subtitle text-xl max-w-2xl mx-auto" data-testid="contact-subtitle">
            {content.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold mb-6" style={{ color: '#8B7355' }}>
                Contact Information
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="contact-icon-wrapper flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1" style={{ color: '#1e1919' }}>Email</p>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-sm hover:underline"
                      style={{ color: '#736c64' }}
                      data-testid="contact-email"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="contact-icon-wrapper flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1" style={{ color: '#1e1919' }}>Phone</p>
                    <a 
                      href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                      className="text-sm hover:underline"
                      style={{ color: '#736c64' }}
                      data-testid="contact-phone"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="contact-icon-wrapper flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1" style={{ color: '#1e1919' }}>Service Area</p>
                    {contactInfo.address.split('\n').map((line, idx) => (
                      <p 
                        key={idx} 
                        className={`text-sm ${idx === 0 ? 'font-semibold' : 'mt-1'}`}
                        style={{ color: idx === 0 ? '#8B7355' : '#736c64' }}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t" style={{ borderColor: '#e5e5e5' }}>
                <p className="text-sm font-semibold mb-3" style={{ color: '#1e1919' }}>
                  Business Hours
                </p>
                <div className="text-sm space-y-1" style={{ color: '#736c64' }}>
                  <p><strong>Monday - Friday:</strong> {businessHours.weekday}</p>
                  <p><strong>Saturday:</strong> {businessHours.saturday}</p>
                  <p><strong>Sunday & Public Holidays:</strong> {businessHours.sunday}</p>
                </div>
                {businessHours.note && (
                  <p className="text-xs mt-3 italic" style={{ color: '#8B7355' }}>
                    Note: {businessHours.note}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <form 
                onSubmit={handleSubmit}
                name="contact"
                method="POST"
                action="/success"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                data-testid="contact-form"
              >
                {/* Hidden field for Netlify form name */}
                <input type="hidden" name="form-name" value="contact" />
                
                {/* Honeypot field for spam protection */}
                <p className="hidden">
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="form-label block text-sm font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      placeholder="John Doe"
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="form-label block text-sm font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="form-label block text-sm font-semibold mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      placeholder="+27 84 123 4567"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="eventType" className="form-label block text-sm font-semibold mb-2">
                      Event Type *
                    </label>
                    <select
                      id="eventType"
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      data-testid="select-event-type"
                    >
                      <option value="">Select event type</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="wedding">Wedding</option>
                      <option value="private">Private Party</option>
                      <option value="concert">Concert/Festival</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="eventDate" className="form-label block text-sm font-semibold mb-2">
                      Event Date
                    </label>
                    <input
                      type="date"
                      id="eventDate"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      data-testid="input-event-date"
                    />
                  </div>

                  <div>
                    <label htmlFor="guestCount" className="form-label block text-sm font-semibold mb-2">
                      Guest Count
                    </label>
                    <input
                      type="number"
                      id="guestCount"
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleChange}
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      placeholder="50"
                      data-testid="input-guest-count"
                    />
                  </div>

                  <div>
                    <label htmlFor="serviceType" className="form-label block text-sm font-semibold mb-2">
                      Service Type *
                    </label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      data-testid="select-service-type"
                    >
                      <option value="">Select service type</option>
                      <option value="self-service">Self-Service Cart</option>
                      <option value="full-service">Full-Service with Servers</option>
                      <option value="unsure">Not Sure Yet</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="packageType" className="form-label block text-sm font-semibold mb-2">
                      Package Preference
                    </label>
                    <select
                      id="packageType"
                      name="packageType"
                      value={formData.packageType}
                      onChange={handleChange}
                      className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                      data-testid="select-package-type"
                    >
                      <option value="">Select package</option>
                      <option value="savory">Savory Selections</option>
                      <option value="sweet">Sweet Indulgence</option>
                      <option value="fruit">Fresh Fruit Bar</option>
                      <option value="custom">Custom Mix</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="form-label block text-sm font-semibold mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="form-input w-full px-4 py-3 rounded-md border transition-all duration-200"
                    placeholder="Tell us more about your event, special requirements, or any questions you have..."
                    data-testid="input-message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button w-full md:w-auto px-8 py-4 rounded-md font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2"
                  data-testid="submit-button"
                >
                  {isSubmitting ? 'Sending...' : content.submit_text || 'Send Enquiry'}
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
